import { createContext, useCallback, useContext, useState } from "react";
import { useGetProduct, useGetProducts } from "@/modules/product";
import { ProductContextType } from "../types";
import { useParams } from "react-router";
import { useCachedPage } from "@/shared/hooks";

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const ProductProvider = ({ children }: { children: React.ReactNode }) => {
	const { productId } = useParams();
	const [cachedPage, setCachedPage] = useCachedPage("products-table");
	const [filter, _setFilter] = useState<Record<string, string | number | boolean>>(() => {
		let cachedFilters: Record<string, string> = {};
		try {
			const stored = sessionStorage.getItem("table_filter_products-filters");
			if (stored) cachedFilters = JSON.parse(stored) as Record<string, string>;
		} catch {
			// ignore malformed data
		}
		return { page: cachedPage, ...cachedFilters };
	});
	const setFilter = useCallback(
		(newFilter: Record<string, string | number | boolean>) => {
			if (typeof newFilter.page === "number") {
				setCachedPage(newFilter.page);
			}
			_setFilter(newFilter);
		},
		[setCachedPage]
	);
	const { data, isLoading, refetch } = useGetProducts(true, filter);

	const { data: product, isLoading: loadingSingleProduct } = useGetProduct(productId ? +productId : 0);

	return (
		<ProductContext.Provider
			value={{
				pages: data?.pages || 0,
				products: data?.result.length ? data.result : [],
				isLoadingTable: isLoading,
				refetchProducts: refetch,
				totalItems: data?.total || 0,
				setFilter,
				loadingSingleProduct,
				product,
				filter,
			}}>
			{children}
		</ProductContext.Provider>
	);
};

// eslint-disable-next-line react-refresh/only-export-components
export const useProductContext = () => {
	const context = useContext(ProductContext);
	if (!context) {
		throw new Error("useProductContext must be used within a ProductProvider");
	}
	return context;
};
